import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { IdbStorageService, IDB_STORE_CACHE } from './idb-storage.service';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { storagePrefix } from '../utils/storage-key';

/**
 * Increment when the cache shape changes (e.g. new fields added).
 * Caches written by an older version are discarded and re-fetched.
 * v4: reason_for_party_not_served now included in the reason cache key.
 * v5: spokeWith/leftWith collapsed into a single `relationship` field, sourced
 * from the consolidated `GET /relationship` (v2) endpoint.
 * v6: the `signature` field was removed — it duplicated
 * `SignatureStorageService`'s `{hash}_esign_data` and was written but never
 * read by anything, so it was pure bloat in this blob (a base64 PNG).
 */
const CACHE_VERSION = 6;

/** Raw API responses for all reference data endpoints, as received from the server. */
export interface ReferenceDataCache {
  v?: number;
  hygieneFactor?: unknown;
  relationship?: unknown;
  badAddressReason?: unknown;
  observation?: unknown;
  reason?: unknown;
}

/**
 * Pre-fetches and caches reference data used by the attempt log form.
 *
 * @remarks
 * Called once after `JobsService.loadJobs()` succeeds so all 6 dropdown lists
 * are ready before the user opens the attempt screen.  `getAll()` returns the
 * cached responses instantly on subsequent calls — no network overhead per job.
 *
 * Storage: the IndexedDB `cache` store under key
 * `{storagePrefix(entityId)}_ref_data_cache` (moved off
 * `capacitor-secure-storage-plugin` 2026-08-05 — see {@link IdbStorageService}).
 * The key is entity-scoped so different users on the same device get isolated caches.
 * Cleared on logout so stale data is never shown after a user switch.
 */
@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  private readonly http        = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly idb         = inject(IdbStorageService);

  /** Returns the entity-scoped storage key, or `null` when no token. */
  private get cacheKey(): string | null {
    const token = this.authService.token();
    if (!token) return null;
    return `${storagePrefix(token.user.entityId)}_ref_data_cache`;
  }

  /**
   * Fetches all 5 reference data endpoints in parallel and persists the raw
   * responses to the IndexedDB `cache` store under the entity-scoped key.
   *
   * Endpoints: hygiene-factor, relationship, bad-address-reason, observation,
   * reason. (`GET /process-server/{id}/esignature` was fetched here until
   * 2026-08-05 — see the note in the method body for why it was dropped.)
   *
   * Uses `Promise.allSettled` — a failed endpoint is silently skipped so the
   * remaining results are still cached. Fire-and-forget safe: errors per
   * endpoint are swallowed individually.
   *
   * **The write MERGES onto the existing cache rather than replacing it
   * (2026-07-31 fix).** This method previously built a brand-new
   * `{ v: CACHE_VERSION }` object, populated only the endpoints that
   * succeeded *this* round, and overwrote the whole stored blob — so a
   * single failed endpoint silently **deleted** that key's previously-good
   * value while leaving `v` current. `getAll()` then treated the result as
   * a valid cache hit and never re-fetched, so the loss was sticky. Since
   * this runs on login, manual refresh, and every auto-sync tick, one flaky
   * response was enough to blank a dropdown until a later tick happened to
   * succeed (reported on Android as an empty/stuck "Reason(s) not served"
   * list, whose options come from `/observation`). Merging is strictly
   * safer than replacing: a *successful* endpoint still overwrites its own
   * key as before, so no staleness is introduced — only a *failed* one now
   * retains its last-known-good value instead of destroying it. An existing
   * cache written by an older `CACHE_VERSION` is deliberately NOT merged
   * (its field shapes may differ) — that case still starts from scratch.
   *
   * @param entityId - Pass the entity ID explicitly to avoid a token-signal
   *   timing issue when called fire-and-forget from `SyncService.fullSync()`.
   *   Falls back to `authService.token()?.user.entityId` when omitted (used by
   *   `getAll()` which runs in a component context where the token is stable).
   * @returns `true` when at least one of the 6 endpoints succeeded and the
   *   merged cache was persisted; `false` when there was no entity ID, every
   *   endpoint failed, or the SecureStorage write itself failed. Callers that
   *   only need background-refresh resilience can ignore the return value —
   *   a failed endpoint never throws out of this method.
   */
  async prefetch(entityId?: number): Promise<boolean> {
    const eid = entityId ?? this.authService.token()?.user.entityId;
    if (!eid) return false;
    const key = `${storagePrefix(eid)}_ref_data_cache`;

    const base = environment.apiUrl;
    // NOTE: `GET /process-server/{id}/esignature` used to be fetched here too and
    // stored as `cache.signature`. Nothing ever read that field — the saved
    // signature is owned by `SignatureStorageService` (`{hash}_esign_data`), and
    // `AffidavitComponent`/`SignAdapter` fetch the endpoint directly — so both the
    // request and the field were removed (2026-08-05, CACHE_VERSION 6).
    const [hygieneRes, relationshipRes, badAddrRes, obsRes, reasonRes] =
      await Promise.allSettled([
        firstValueFrom(this.http.get<unknown>(`${base}/hygiene-factor`)),
        firstValueFrom(this.http.get<unknown>(`${base}/relationship`)),
        firstValueFrom(this.http.get<unknown>(`${base}/bad-address-reason`)),
        firstValueFrom(this.http.get<unknown>(`${base}/observation`)),
        firstValueFrom(this.http.get<unknown>(`${base}/reason`)),
      ]);

    // Start from the last-known-good cache so an endpoint that failed this
    // round keeps its previous value instead of being wiped (see @remarks).
    const existing = await this.readCache(key);
    const cache: ReferenceDataCache =
      existing?.v === CACHE_VERSION ? { ...existing, v: CACHE_VERSION } : { v: CACHE_VERSION };

    if (hygieneRes.status      === 'fulfilled') cache.hygieneFactor    = hygieneRes.value;
    if (relationshipRes.status === 'fulfilled') cache.relationship     = relationshipRes.value;
    if (badAddrRes.status      === 'fulfilled') cache.badAddressReason = badAddrRes.value;
    if (obsRes.status          === 'fulfilled') cache.observation      = obsRes.value;
    if (reasonRes.status       === 'fulfilled') cache.reason           = reasonRes.value;

    const anySucceeded = [hygieneRes, relationshipRes, badAddrRes, obsRes, reasonRes]
      .some(r => r.status === 'fulfilled');

    // IndexedDB stores the object by structured clone, so no JSON/URI encoding
    // is needed on that path. The legacy fallback keeps encodeURIComponent:
    // capacitor-secure-storage-plugin calls btoa() internally, which throws on
    // characters outside Latin-1 (e.g. em-dashes in reason names).
    await this.idb.setOrFallback(
      IDB_STORE_CACHE,
      key,
      cache,
      () => encodeURIComponent(JSON.stringify(cache)),
    );

    return anySucceeded;
  }

  /**
   * Returns reference data from SecureStorage cache if available.
   * Falls back to a fresh network fetch when the cache is absent or unreadable.
   *
   * @remarks
   * Called by `AttemptLogComponent.loadReferenceData()` on every attempt screen
   * open.  The cache hit path reads from SecureStorage with no network overhead.
   */
  async getAll(): Promise<ReferenceDataCache> {
    const cached = await this.readCache();
    if (cached && cached.v === CACHE_VERSION) return cached;
    // Cache absent or written by an older version — re-fetch.
    const eid = this.authService.token()?.user.entityId;
    await this.prefetch(eid);
    return (await this.readCache()) ?? {};
  }

  /**
   * Removes the reference data cache from SecureStorage.
   *
   * @remarks
   * Pass `entityId` explicitly — the token is already null when the logout
   * effect fires, so reading `this.cacheKey` at that point returns null and
   * the remove is silently skipped.  Callers must capture the entity ID
   * before the token clears (same pattern as `JobsService.clearCache`).
   *
   * @param entityId - The numeric entity ID of the user logging out.
   */
  clearCache(entityId: number): void {
    const key = `${storagePrefix(entityId)}_ref_data_cache`;
    void this.idb.removeEverywhere(IDB_STORE_CACHE, key);
  }

  /**
   * Reads and decodes the persisted cache blob.
   *
   * @param key - SecureStorage key to read. Defaults to the token-derived
   *   {@link cacheKey}; `prefetch()` passes its own explicitly-resolved key
   *   instead, since it may be called fire-and-forget before the token
   *   signal has settled.
   * @returns The parsed cache, or `null` when there is no key, no stored
   *   value, or the value cannot be parsed.
   */
  private async readCache(key: string | null = this.cacheKey): Promise<ReferenceDataCache | null> {
    if (!key) return null;
    return this.idb.getOrMigrate<ReferenceDataCache>(
      IDB_STORE_CACHE,
      key,
      (raw) => JSON.parse(decodeURIComponent(raw)) as ReferenceDataCache,
    );
  }
}
